// pieces and components
const initReplaceContentForMobile = (target, originalContent, newContent) => {
	function mobileContent() {
		target.innerText = newContent;
	}

	isMobile ? mobileContent() : '';

	window.addEventListener('resize', function () {
		if (isMobile) {
			mobileContent();
		} else {
			target.innerText = originalContent;
		}
	});
};

const initComboBox = () => {
	'use strict';

	// Save a list of named combobox actions, for future readability
	const SelectActions = {
		Close: 0,
		CloseSelect: 1,
		First: 2,
		Last: 3,
		Next: 4,
		Open: 5,
		PageDown: 6,
		PageUp: 7,
		Previous: 8,
		Select: 9,
		Type: 10,
	};

	/*
	 * Helper functions
	 */

	// filter an array of options against an input string
	// returns an array of options that begin with the filter string, case-independent
	function filterOptions(options = [], filter, exclude = []) {
		return options.filter((option) => {
			const matches = option.toLowerCase().indexOf(filter.toLowerCase()) === 0;
			return matches && exclude.indexOf(option) < 0;
		});
	}

	// map a key press to an action
	function getActionFromKey(event, menuOpen) {
		const { key, altKey, ctrlKey, metaKey } = event;
		const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' ']; // all keys that will do the default open action
		// handle opening when closed
		if (!menuOpen && openKeys.includes(key)) {
			return SelectActions.Open;
		}

		// home and end move the selected option when open or closed
		if (key === 'Home') {
			return SelectActions.First;
		}
		if (key === 'End') {
			return SelectActions.Last;
		}

		// handle typing characters when open or closed
		if (
			key === 'Backspace' ||
			key === 'Clear' ||
			(key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey)
		) {
			return SelectActions.Type;
		}

		// handle keys when open
		if (menuOpen) {
			if (key === 'ArrowUp' && altKey) {
				return SelectActions.CloseSelect;
			} else if (key === 'ArrowDown' && !altKey) {
				return SelectActions.Next;
			} else if (key === 'ArrowUp') {
				return SelectActions.Previous;
			} else if (key === 'PageUp') {
				return SelectActions.PageUp;
			} else if (key === 'PageDown') {
				return SelectActions.PageDown;
			} else if (key === 'Escape') {
				return SelectActions.Close;
			} else if (key === 'Enter' || key === ' ') {
				return SelectActions.CloseSelect;
			}
		}
	}

	// return the index of an option from an array of options, based on a search string
	// if the filter is multiple iterations of the same letter (e.g "aaa"), then cycle through first-letter matches
	function getIndexByLetter(options, filter, startIndex = 0) {
		const orderedOptions = [
			...options.slice(startIndex),
			...options.slice(0, startIndex),
		];
		const firstMatch = filterOptions(orderedOptions, filter)[0];
		const allSameLetter = (array) =>
			array.every((letter) => letter === array[0]);

		// first check if there is an exact match for the typed string
		if (firstMatch) {
			return options.indexOf(firstMatch);
		}

		// if the same letter is being repeated, cycle through first-letter matches
		else if (allSameLetter(filter.split(''))) {
			const matches = filterOptions(orderedOptions, filter[0]);
			return options.indexOf(matches[0]);
		}

		// if no matches, return -1
		else {
			return -1;
		}
	}

	// get an updated option index after performing an action
	function getUpdatedIndex(currentIndex, maxIndex, action) {
		const pageSize = 10; // used for pageup/pagedown

		switch (action) {
			case SelectActions.First:
				return 0;
			case SelectActions.Last:
				return maxIndex;
			case SelectActions.Previous:
				return Math.max(0, currentIndex - 1);
			case SelectActions.Next:
				return Math.min(maxIndex, currentIndex + 1);
			case SelectActions.PageUp:
				return Math.max(0, currentIndex - pageSize);
			case SelectActions.PageDown:
				return Math.min(maxIndex, currentIndex + pageSize);
			default:
				return currentIndex;
		}
	}

	// check if element is visible in browser view port
	function isElementInView(element) {
		var bounding = element.getBoundingClientRect();

		return (
			bounding.top >= 0 &&
			bounding.left >= 0 &&
			bounding.bottom <=
				(window.innerHeight || document.documentElement.clientHeight) &&
			bounding.right <=
				(window.innerWidth || document.documentElement.clientWidth)
		);
	}

	// check if an element is currently scrollable
	function isScrollable(element) {
		return element && element.clientHeight < element.scrollHeight;
	}

	// check if on mobile or tablet device
	function isMobileOrTablet() {
		const userAgent = navigator.userAgent.toLowerCase();

		return (
			/iPhone|Android/i.test(navigator.userAgent) ||
			/(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(
				userAgent
			)
		);
	}

	// ensure a given child element is within the parent's visible scroll area
	// if the child is not visible, scroll the parent
	function maintainScrollVisibility(activeElement, scrollParent) {
		const { offsetHeight, offsetTop } = activeElement;
		const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

		const isAbove = offsetTop < scrollTop;
		const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

		if (isAbove) {
			scrollParent.scrollTo(0, offsetTop);
		} else if (isBelow) {
			scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
		}
	}

	/*
	 * Select Component
	 * Accepts a combobox element and an array of string options
	 */
	const Select = function (el, options = []) {
		// element refs
		this.el = el;
		this.selector = this.el.querySelector('select');
		this.comboBox = el.querySelector('.combobox');
		this.comboEl = document.createElement('div');
		this.listboxEl = document.createElement('div');

		this.comboEl.setAttribute('aria-controls', `${this.el.id}-listbox`);
		this.comboEl.setAttribute('aria-expanded', false);
		this.comboEl.setAttribute('aria-haspopup', 'listbox');
		this.comboEl.setAttribute('aria-labelledby', `${this.el.id}-label`);
		this.comboEl.setAttribute('role', 'combobox');
		this.comboEl.setAttribute('tabindex', 0);
		this.comboEl.id = `${this.el.id}-input`;
		this.comboEl.classList.add('combobox-input');
		this.comboBox.prepend(this.comboEl);

		this.listboxEl.setAttribute('aria-labelledby', `${this.el.id}-label`);
		this.listboxEl.setAttribute('role', 'listbox');
		this.listboxEl.setAttribute('tabindex', '-1');
		this.listboxEl.id = `${this.el.id}-listbox`;
		this.listboxEl.classList.add('combobox-menu');
		this.comboBox.prepend(this.listboxEl);

		// data
		this.idBase = this.comboEl.id || 'combo';
		this.options = options;

		// state
		this.activeIndex = 0;
		this.open = false;
		this.searchString = '';
		this.searchTimeout = null;

		// init
		if (el && this.comboEl && this.listboxEl) {
			this.init();

			// assign ID to <label> and <select>
			this.el
				.querySelector('label')
				.setAttribute('for', `${this.el.id}-select`);
			this.selector.id = `${this.el.id}-select`;

			// match <select> actions to combobox
			this.selector.addEventListener('change', (event) => {
				event.stopPropagation();
				this.onOptionClick(this.selector.selectedIndex);
			});

			if (isMobileOrTablet()) {
				this.listboxEl.setAttribute('aria-hidden', true);
			} else {
				this.el.classList.add('is-select-hidden');
			}
		}
	};

	Select.prototype.init = function () {
		// select first option by default
		this.comboEl.innerHTML = this.options[0];

		// add event listeners
		this.comboEl.addEventListener('blur', this.onComboBlur.bind(this));
		this.comboEl.addEventListener('click', this.onComboClick.bind(this));
		this.comboEl.addEventListener('keydown', this.onComboKeyDown.bind(this));

		// create options
		this.options.map((option, index) => {
			const optionEl = this.createOption(option, index);
			this.listboxEl.appendChild(optionEl);
		});
	};

	Select.prototype.createOption = function (optionText, index) {
		const optionEl = document.createElement('div');
		optionEl.setAttribute('role', 'option');
		optionEl.id = `${this.idBase}-${index}`;
		optionEl.className =
			index === 0 ? 'combobox-option is-option-current' : 'combobox-option';
		optionEl.setAttribute('aria-selected', `${index === 0}`);
		optionEl.innerText = optionText;

		optionEl.addEventListener('click', (event) => {
			event.stopPropagation();
			this.onOptionClick(index);
		});
		optionEl.addEventListener('mousedown', this.onOptionMouseDown.bind(this));

		return optionEl;
	};

	Select.prototype.getSearchString = function (char) {
		// reset typing timeout and start new timeout
		// this allows us to make multiple-letter matches, like a native select
		if (typeof this.searchTimeout === 'number') {
			window.clearTimeout(this.searchTimeout);
		}

		this.searchTimeout = window.setTimeout(() => {
			this.searchString = '';
		}, 500);

		// add most recent letter to saved search string
		this.searchString += char;
		return this.searchString;
	};

	Select.prototype.onComboBlur = function () {
		// do not do blur action if ignoreBlur flag has been set
		if (this.ignoreBlur) {
			this.ignoreBlur = false;
			return;
		}

		// select current option and close
		if (this.open) {
			this.selectOption(this.activeIndex);
			this.updateMenuState(false, false);
		}
	};

	Select.prototype.onComboClick = function () {
		this.updateMenuState(!this.open, false);
	};

	Select.prototype.onComboKeyDown = function (event) {
		const { key } = event;
		const max = this.options.length - 1;

		const action = getActionFromKey(event, this.open);

		switch (action) {
			case SelectActions.Last:
			case SelectActions.First:
				this.updateMenuState(true);
			// intentional fallthrough

			case SelectActions.Next:
			case SelectActions.Previous:
			case SelectActions.PageUp:
			case SelectActions.PageDown:
				event.preventDefault();
				return this.onOptionChange(
					getUpdatedIndex(this.activeIndex, max, action)
				);
			case SelectActions.CloseSelect:
				event.preventDefault();
				this.selectOption(this.activeIndex);
			// intentional fallthrough

			case SelectActions.Close:
				event.preventDefault();
				return this.updateMenuState(false);
			case SelectActions.Type:
				return this.onComboType(key);
			case SelectActions.Open:
				event.preventDefault();
				return this.updateMenuState(true);
		}
	};

	Select.prototype.onComboType = function (letter) {
		// open the listbox if it is closed
		this.updateMenuState(true);

		// find the index of the first matching option
		const searchString = this.getSearchString(letter);
		const searchIndex = getIndexByLetter(
			this.options,
			searchString,
			this.activeIndex + 1
		);

		// if a match was found, go to it
		if (searchIndex >= 0) {
			this.onOptionChange(searchIndex);
		}
		// if no matches, clear the timeout and search string
		else {
			window.clearTimeout(this.searchTimeout);
			this.searchString = '';
		}
	};

	Select.prototype.onOptionChange = function (index) {
		// update state
		this.activeIndex = index;

		// update aria-activedescendant
		this.comboEl.setAttribute(
			'aria-activedescendant',
			`${this.idBase}-${index}`
		);

		// update active option styles
		const options = this.el.querySelectorAll('[role=option]');
		[...options].forEach((optionEl) => {
			optionEl.classList.remove('is-option-current');
		});
		options[index].classList.add('is-option-current');

		// ensure the new option is in view
		if (isScrollable(this.listboxEl)) {
			maintainScrollVisibility(options[index], this.listboxEl);
		}

		// ensure the new option is visible on screen
		// ensure the new option is in view
		if (!isElementInView(options[index])) {
			options[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	};

	Select.prototype.onOptionClick = function (index) {
		this.onOptionChange(index);
		this.selectOption(index);
		this.updateMenuState(false);
	};

	Select.prototype.onOptionMouseDown = function () {
		// Clicking an option will cause a blur event,
		// but we don't want to perform the default keyboard blur action
		this.ignoreBlur = true;
	};

	Select.prototype.selectOption = function (index) {
		// update state
		this.activeIndex = index;

		// update displayed value
		const selected = this.options[index];
		this.comboEl.innerHTML = selected;

		// update aria-selected
		const options = this.el.querySelectorAll('[role=option]');
		[...options].forEach((optionEl) => {
			optionEl.setAttribute('aria-selected', 'false');
		});
		options[index].setAttribute('aria-selected', 'true');

		// update <select>
		this.selector.value = this.selector.querySelectorAll('option')[index].value;
	};

	Select.prototype.updateMenuState = function (open, callFocus = true) {
		if (this.open === open) {
			return;
		}

		// update state
		this.open = open;

		// update aria-expanded and styles
		this.comboEl.setAttribute('aria-expanded', `${open}`);
		open
			? this.el.classList.add('is-open')
			: this.el.classList.remove('is-open');

		// update activedescendant
		const activeID = open ? `${this.idBase}-${this.activeIndex}` : '';
		this.comboEl.setAttribute('aria-activedescendant', activeID);

		if (activeID === '' && !isElementInView(this.comboEl)) {
			this.comboEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}

		// move focus back to the selector, if needed
		callFocus && this.comboEl.focus();
	};

	// init select
	document.querySelectorAll('.combobox-selector').forEach((el) => {
		let options = [];

		el.querySelectorAll('option').forEach((option) => {
			options.push(option.innerText);
		});

		new Select(el, options);
	});
};

const initAccordions = () => {
	const accordionItems = document.querySelectorAll('.accordion');

	const closeAccordions = (targetCompAccordion) => {
		accordionItems.forEach((accordionItem) => {
			const compAccordions = accordionItem.querySelectorAll('.comp-accordion');
			compAccordions.forEach((compAccordion) => {
				if (targetCompAccordion != compAccordion) {
					slide('up', compAccordion.querySelector('.accordion-content'));
					compAccordion.classList.remove('is-expanded');
					compAccordion.setAttribute('aria-expanded', false);
					compAccordion
						.querySelector('.accordion-content')
						.setAttribute('aria-hidden', true);
				}
			});
		});
	};

	// change navigation list dot's color
	const navListDotColor = (id) => {
		const lists = document.querySelectorAll('.panel-bottom .list-item');

		if (lists) {
			lists.forEach((list) => {
				list.dataset.id.includes(id)
					? list.classList.toggle('is-active')
					: list.classList.remove('is-active');
			});
		}
	};

	const alignAccordion = (targetCompAccordion) => {
		if (isMobile || isTablet) {
			seamless.scrollIntoView(targetCompAccordion.querySelector('.anchor'), {
				behavior: 'smooth',
				block: 'start',
				inline: 'nearest',
			});
		} else {
			seamless.scrollBy(window, {
				behavior: 'smooth',
				top: targetCompAccordion
					.querySelector('.anchor')
					.getBoundingClientRect().top,
				left: 0,
			});
		}
	};

	// open target accordion
	const toggleAccordion = (targetCompAccordion, isAnyAccordionActive) => {
		const accordionContent =
			targetCompAccordion.querySelector('.accordion-content');
		const timeOut =
			isAnyAccordionActive == false ? 0 : isMobile || isTablet ? 310 : 400;

		targetCompAccordion.classList.toggle('is-expanded');
		slide('toggle', accordionContent);

		targetCompAccordion.querySelector('.anchor')
			? setTimeout(() => {
					alignAccordion(targetCompAccordion);
			  }, timeOut)
			: '';

		if (targetCompAccordion.classList.contains('is-expanded')) {
			targetCompAccordion.setAttribute('aria-expanded', true);
			accordionContent.setAttribute('aria-hidden', false);
		} else {
			targetCompAccordion.setAttribute('aria-expanded', false);
			accordionContent.setAttribute('aria-hidden', true);
		}
	};

	on('body', 'click', '.panel-bottom .list-item', (e) => {
		const listItem = e.target.closest('.list-item');
		const targetID = listItem.dataset.id;
		const targetCompAccordion = document
			.querySelector(`#${targetID}`)
			.closest('.comp-accordion');

		listItem.classList.toggle('is-active');

		getSiblings(listItem).forEach((item) => {
			item.classList.remove('is-active');
		});

		closeAccordions(targetCompAccordion);
		toggleAccordion(targetCompAccordion);
	});

	on('body', 'click', '.js-toggle-accordion', (e) => {
		const targetCompAccordion = e.target.closest('.comp-accordion');
		const id = targetCompAccordion.querySelector('.anchor')
			? targetCompAccordion.querySelector('.anchor').id
			: '';
		const isAnyAccordionActive = document.querySelector(
			'.accordion-content.is-active'
		)
			? true
			: false;

		closeAccordions(targetCompAccordion);
		toggleAccordion(targetCompAccordion, isAnyAccordionActive);
		id ? navListDotColor(id) : '';
	});

	window.addEventListener('resize', () => {
		const targetContent = document.querySelector('.accordion-content.is-active')
			? document.querySelector('.accordion-content.is-active')
			: '';

		if (targetContent) {
			let target =
				targetContent.querySelector('.dashed-line').nextElementSibling;
			let compTarget = window.getComputedStyle(target);
			let compTargetHeight = compTarget.getPropertyValue('height');
			console.log(compTargetHeight);
			targetContent.style.height = compTargetHeight;
		}
	});
};

const initFlickitySlider = () => {
	const flickitySlider = document.querySelectorAll('[data-flickity-slider]');

	const flickitySliderTrigger = () => {
		flickitySlider.forEach((el) => {
			let flickity = new Flickity(
				el,
				JSON.parse(el.dataset.flickitySlider.replace(/'/g, '"').trim())
			);
			let childrenWidth = Object.values(
				el.querySelector('.flickity-slider').childNodes
			).reduce(
				(total, i) =>
					i.offsetWidth
						? total +
						  i.offsetWidth +
						  parseInt(window.getComputedStyle(i).marginLeft) +
						  parseInt(window.getComputedStyle(i).marginRight)
						: total,
				0
			);

			if (
				// if total children width is lesser than slider width
				el.getBoundingClientRect().width >= childrenWidth ||
				// if flickity slider has breakpoint set, and window is greater than breakpoint
				(el.dataset.flickityBreakpoint &&
					window.innerWidth > parseInt(el.dataset.flickityBreakpoint))
			) {
				// destroy flickity
				flickity.destroy();
				el.classList.add('flickity-disabled');

				setTimeout(() => {
					el.querySelectorAll('[style*="left:"]').forEach((item) => {
						item.style.setProperty('left', 'unset');
					});
				}, 100);
			} else {
				// avoid click on drag: https://github.com/metafizzy/flickity/issues/838
				flickity.on('dragStart', () =>
					flickity.slider.childNodes.forEach(
						(slide) => (slide.style.pointerEvents = 'none')
					)
				);

				flickity.on('dragEnd', () =>
					flickity.slider.childNodes.forEach(
						(slide) => (slide.style.pointerEvents = 'all')
					)
				);

				// child height equalizer
				if (el.classList.contains('flickity-child-is-equal-height')) {
					let maxHeight = [];

					el.querySelector('.flickity-slider').childNodes.forEach((item) => {
						maxHeight.push(item.offsetHeight);
					});

					el.querySelector('.flickity-slider').childNodes.forEach((item) => {
						item.style.height = `${Math.max.apply(Math, maxHeight)}px`;
					});
				}
			}
		});
	};

	const initialWidth = window.innerWidth;

	flickitySliderTrigger();

	// window.addEventListener('resize', () => {
	// 	if (window.innerWidth != initialWidth) {
	// 		// re-init flickity only in this case
	// 		flickitySliderTrigger();
	// 	}
	// });

	// swipe fix for IOS
	var touchingCarousel = false;
	var touchStartCoords;

	document.body.addEventListener('touchstart', function (e) {
		if (e.target.closest('.flickity-slider')) {
			touchingCarousel = true;
		} else {
			touchingCarousel = false;
			return;
		}

		touchStartCoords = {
			x: e.touches[0].pageX,
			y: e.touches[0].pageY,
		};
	});

	document.body.addEventListener(
		'touchmove',
		function (e) {
			if (!(touchingCarousel && e.cancelable)) {
				return;
			}

			var moveVector = {
				x: e.touches[0].pageX - touchStartCoords.x,
				y: e.touches[0].pageY - touchStartCoords.y,
			};

			if (Math.abs(moveVector.x) > 7) {
				e.preventDefault();
			}
		},
		{
			passive: false,
		}
	);
};

const initMarquee = () => {
	const marquees = document.querySelectorAll('.marquee');

	marquees.forEach((marquee) => {
		setTimeout(() => {
			// block content must be greater the window's innerWidth
			const marqueeContent = marquee.querySelectorAll('.marquee-content');
			const maruqeeBlockWidth = marqueeContent[0].clientWidth;
			const factor = Math.ceil(innerWidth / maruqeeBlockWidth);
			const marqueeHtml = marqueeContent[0].innerHTML;
			// const pixelPerSecond = 150;
			// const speed = (maruqeeBlockWidth * factor / pixelPerSecond) * (marquee.dataset.speedFactor || 1);

			marqueeContent.forEach((block) => {
				// override default speed if specified?
				// block.style.animationDuration = `${parseInt(speed)}s`;

				// should we replicate content to fill the window's innerWidth?
				if (isFinite(factor)) {
					for (i = 0; i < factor; i++) {
						// if so, repeat block content
						block.insertAdjacentHTML('beforeend', marqueeHtml);
					}
				}
			});
		}, 1);
	});
};

const initFields = () => {
	const fields = document.querySelectorAll('.field');

	fields.forEach((item) => {
		let input = item.querySelector(`input[type="text"],
			input[type="email"],
			input[type="tel"],
			input[type="url"],
			input[type="password"],
			input[type="file"],
			textarea`);
		let determineHasValue = () => {
			input.value
				? item.classList.add('has-value')
				: item.classList.remove('has-value');
		};

		if (input) {
			determineHasValue();
			input.addEventListener('change', determineHasValue);
			input.addEventListener('blur', determineHasValue);
		}
	});
};

// global elements and pages
const initArrows = () => {
	const arrowRights = document.querySelectorAll('.svg-arrow-right');
	const arrowLefts = document.querySelectorAll('.svg-arrow-left');

	function mobileArrows() {
		arrowRights.forEach((arrowRight) => {
			arrowRight.innerHTML =
				'<svg class="svg-arrow-right" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 18"><path d="M1.25 9h22M15.55 17.25v0a15.58 15.58 0 0 1 7.498-8.15l.202-.1-.202-.1A15.58 15.58 0 0 1 15.55.75v0" stroke="#3E2B2E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
		});
		arrowLefts.forEach((arrowLeft) => {
			arrowLeft.innerHTML =
				'<svg class="svg-arrow-left" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 18"><path d="M23.25 9h-22M8.95.75v0A15.58 15.58 0 0 1 1.452 8.9L1.25 9l.202.1a15.58 15.58 0 0 1 7.498 8.15v0" stroke="#3E2B2E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
		});
	}

	isMobile ? mobileArrows() : '';

	window.addEventListener('resize', function () {
		if (isMobile) {
			mobileArrows();
		} else {
			arrowRights.forEach((arrowRight) => {
				arrowRight.innerHTML =
					'<svg class="svg-arrow-right" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 42 32"><path d="M1 16h40M27 1l1.176 2.94A20 20 0 0 0 37.8 14.4L41 16l-3.199 1.6a20 20 0 0 0-9.625 10.46L27 31" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
			});
			arrowLefts.forEach((arrowLeft) => {
				arrowLeft.innerHTML =
					'<svg class="svg-arrow-left" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 42 32"><path d="M41 16H1M15 31l-1.176-2.94A20 20 0 0 0 4.2 17.6L1 16l3.199-1.6a20 20 0 0 0 9.625-10.46L15 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
			});
		}
	});
};

const initPopUpMessage = () => {
	// show pop up - after you scroll past the “our menu” section
	const messageId = 'vs-taim-popup-message';
	const globalMessage = document.querySelector('.global-message');
	const elementTarget = document.querySelector('.js-popup-target');
	const timeNow = new Date().getTime();
	const timeId = 'vs-taim-popup-message-setup-time';
	const timeSetup = localStorage.getItem(timeId);

	if (globalMessage) {
		if (timeSetup === null) {
			localStorage.setItem(timeId, timeNow);
		} else if (timeNow - timeSetup > 3 * 24 * 60 * 60 * 1000) {
			localStorage.removeItem(messageId);
			localStorage.setItem(timeId, timeNow);
		}

		function popupScroll() {
			if (
				hasLocalStorage &&
				localStorage.getItem(messageId) !== 'closed' &&
				window.scrollY >
					elementTarget.offsetTop + elementTarget.offsetHeight * 0.7
			) {
				root.classList.add('popup-is-active');
			}
		}

		window.addEventListener('scroll', popupScroll);

		// close pop up
		on('body', 'click', '.global-message .js-popup-close', (e) => {
			root.classList.remove('popup-is-active');
			localStorage.setItem(messageId, 'closed');
		});
	}
};

const initEmailCapture = () => {
	// Serialize the form data into a query string
	// Forked and modified from https://stackoverflow.com/a/30153391/1293256
	const serialize = (form) => {
		// Setup our serialized data
		let serialized = '';

		// Loop through each field in the form
		for (i = 0; i < form.elements.length; i++) {
			let field = form.elements[i];

			// Don't serialize fields without a name, submits, buttons, file and reset inputs, and disabled fields
			if (
				!field.name ||
				field.disabled ||
				field.type === 'file' ||
				field.type === 'reset' ||
				field.type === 'submit' ||
				field.type === 'button'
			)
				continue;

			// Convert field data to a query string
			if (
				(field.type !== 'checkbox' && field.type !== 'radio') ||
				field.checked
			) {
				serialized +=
					'&' +
					encodeURIComponent(field.name) +
					'=' +
					encodeURIComponent(field.value);
			}
		}

		return serialized;
	};

	// Display the form status
	window.displayMailChimpStatus = function (data) {
		console.log('🚀 ~ file: js-index.js ~ initEmailCapture ~ data', data);
		// Make sure the data is in the right format and that there's a status container
		if (!data.result || !data.msg || !mcStatus) return;

		// Update our status message
		const mcMessage = data.msg.split('-')[1]
			? data.msg.split('-')[1]
			: data.msg;
		mcStatus.innerHTML = mcMessage;

		// If error, add error class
		if (data.result === 'error') {
			mcStatus.classList.remove('success-message');
			mcStatus.classList.add('error-message');
			return;
		}

		// Otherwise, add success class
		mcStatus.classList.remove('error-message');
		mcStatus.classList.add('success-message');
	};

	// Submit the form
	const submitMailChimpForm = (form) => {
		// Get the Submit URL
		let url = form.getAttribute('action');
		url = url.replace('/post?u=', '/post-json?u=');
		url += serialize(form) + '&c=displayMailChimpStatus';

		// Create script with url and callback (if specified)
		const ref = window.document.getElementsByTagName('script')[0];
		const script = window.document.createElement('script');
		script.src = url;

		// Create a global variable for the status container
		window.mcStatus = form.querySelector('.mc-status');

		// Insert script tag into the DOM (append to <head>)
		ref.parentNode.insertBefore(script, ref);

		// After the script is loaded (and executed), remove it
		script.onload = function () {
			this.remove();
		};
	};

	// Check all fields on submit
	document.addEventListener('submit', (e) => {
		// Only run on forms flagged for validation
		if (!e.target.classList.contains('validate')) return;

		// Prevent form from submitting
		e.preventDefault();

		// Otherwise, let the form submit normally
		// You could also bolt in an Ajax form submit process here
		submitMailChimpForm(e.target);
	});
};

const initPageTransition = () => {
	document.querySelectorAll('a[href]').forEach((item) => {
		// if href points to anchor, or points to anchor that exists on page
		if (
			item.href.startsWith('#') ||
			(item.href.split('#')[1] &&
				item.href.split('#')[0] == window.location.href.split('#')[0])
		) {
			item.setAttribute('data-page-transition', 'scroll');
		} else if (
			(item.href.includes(siteUrl) || item.href.startsWith('/')) &&
			item.target != '_blank' &&
			!item.href.includes('mailto:')
		) {
			item.setAttribute('data-page-transition', 'internal');
		} else {
			item.rel = 'noopener';
			item.target = item.target || '_blank';
		}
	});

	document.querySelectorAll('[data-page-transition]').forEach((item) => {
		item.addEventListener('click', (e) => {
			let target = e.target.hasAttribute('data-page-transition')
				? e.target
				: e.target.closest('[data-page-transition]');

			target.blur();

			if (
				target.dataset.pageTransition == 'internal' &&
				!root.classList.contains('key-is-down')
			) {
				e.preventDefault();
				root.classList.remove(
					'header-is-sticky',
					'menu-is-active',
					'mobile-menu-is-active',
					'minicart-is-active'
				);
				root.classList.add('is_leaving');

				setTimeout(() => {
					location.href = target.href;
				}, 400);
			} else if (target.dataset.pageTransition == 'scroll') {
				e.preventDefault();
				seamless.elementScrollIntoView(
					document.querySelector(`#${target.href.split('#')[1]}`),
					{
						behavior: 'smooth',
					}
				);
			}
		});
	});

	window.addEventListener('keydown', () => {
		root.classList.add('key-is-down');
	});

	window.addEventListener('keyup', () => {
		root.classList.remove('key-is-down');
	});
};

const initPageAnimation = () => {
	document.querySelectorAll('[data-animate]').forEach((item) => {
		let observer = new IntersectionObserver(
			(el) => {
				if (
					el[0].isIntersecting === true &&
					!el[0].target.classList.contains('is-animated')
				) {
					el[0].target.classList.add('is-animated');
				}
			},
			{
				root: null,
				threshold: [0.4], // 0 = page start; 1 = page bottom, items only revealed when in full view
			}
		);

		observer.observe(item);
	});
};

const initContentLayout = () => {
	// iframe fit vids
	reframe(
		'.content-layout iframe[src*="vimeo.com"], .content-layout iframe[src*="youtube.com"]',
		'video-reframe'
	);

	// unwrap image from paragraph tag
	let contentLayout = document.querySelector('.content-layout');
	if (contentLayout) {
		[].forEach.call(contentLayout.getElementsByTagName('img'), function (img) {
			let p = img.closest('p');

			if (p && p.parentElement) {
				p.parentElement.insertBefore(img, p);
				p.parentElement.removeChild(p);
			}
		});
	}
};

const initCart = () => {
	// cart visibility
	const minicartClose = () => {
		root.classList.remove('minicart-is-active');
		history.pushState(null, null, ' ');
		scrollEnable();
	};

	const minicartOpen = () => {
		root.classList.add('minicart-is-active');
		history.pushState(null, null, '#cart');
		scrollDisable();
	};

	on('body', 'click', '.minicart-open', (e) => {
		root.classList.contains('minicart-is-active')
			? minicartClose()
			: minicartOpen();
	});

	on('body', 'click', '.minicart-close', (e) => {
		minicartClose();
	});

	document.addEventListener('keydown', (e) => {
		if (e.key == 'Escape') {
			minicartClose();
		}
	});

	// trigger cart if url contains the hash
	if (location.hash == '#cart') {
		minicartOpen();
	}

	// updates both cart price and item count
	this.reloadCartData = () => {
		fetch('/cart.js')
			.then((response) => {
				return response.json();
			})
			.then((data) => {
				document.querySelectorAll('.cart-total-price').forEach((item) => {
					item.innerText = `$${parseFloat(data.total_price / 100).toFixed(2)}`;
				});

				document.querySelectorAll('.cart-total-items').forEach((item) => {
					item.innerText = data.item_count;
				});

				data.item_count > 0
					? root.classList.add('cart-has-items')
					: root.classList.remove('cart-has-items');
			})
			.catch((error) => {
				console.error('Error:', error);
			});
	};

	// updates .cart-items HTML
	this.reloadCartItems = () => {
		reloadCartData();

		fetch('/cart')
			.then((response) => {
				return response.text();
			})
			.then((data) => {
				var parser = new DOMParser();
				var dataHtml = parser.parseFromString(data, 'text/html');

				// insert the new cart items
				document.querySelectorAll('.cart-items-wrapper').forEach((item) => {
					item.innerHTML = dataHtml.querySelector(
						'.cart-items-wrapper'
					).innerHTML;
				});
			})
			.catch((error) => {
				console.error('Error:', error);
			});
	};

	// use this function to add items
	this.addItem = (
		variantId,
		quantity,
		updateCartTotal = true,
		updateCartItems = true,
		minicartOpenDelay = 400
	) => {
		fetch('/cart/add.js', {
			method: 'POST',
			body: JSON.stringify({
				id: variantId,
				quantity: quantity,
			}),
			headers: {
				'Content-Type': 'application/json',
				'X-Requested-With': 'xmlhttprequest',
			},
			credentials: 'same-origin',
		})
			.then((response) => {
				response.json();
			})
			.then((data) => {
				updateCartTotal ? reloadCartData() : '';
				updateCartItems ? reloadCartItems() : '';
				minicartOpenDelay
					? setTimeout(() => {
							minicartOpen();
					  }, minicartOpenDelay)
					: '';
			})
			.catch((error) => {
				console.error('Error:', error);
			});
	};

	// use this function to change item quantity
	this.changeItemQuantity = (
		line,
		quantity,
		updateCartTotal = true,
		updateCartItems = false,
		minicartOpenDelay = 400
	) => {
		fetch('/cart/change.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			body: JSON.stringify({
				line: line,
				quantity: quantity,
			}),
		})
			.then((response) => {
				response.json();
			})
			.then(() => {
				updateCartTotal ? reloadCartData() : '';
				updateCartItems ? reloadCartItems() : '';
				minicartOpenDelay
					? setTimeout(() => {
							minicartOpen();
					  }, minicartOpenDelay)
					: '';
			})
			.catch((error) => {
				console.error('Error:', error);
			});
	};

	on('body', 'click', '[data-quantity-trigger]', (e) => {
		let target = e.target.closest('[data-quantity-trigger]');
		let item = e.target.closest('.cart-item');

		if (item) {
			item.querySelectorAll('[name="quantity"]').forEach((item) => {
				item.value = target
					.closest('.item-quantity')
					.querySelector('[name="quantity"]').value;
			});

			changeItemQuantity(
				item.dataset.itemLine,
				item.querySelector('[name="quantity"]').value,
				true,
				false,
				false
			);
		}
	});

	on('body', 'click', '.item-remove-trigger', (e) => {
		let item = e.target.closest('.cart-item');

		if (item) {
			item.remove();
			changeItemQuantity(item.dataset.itemLine, 0, true, true, false);
		}
	});

	on('body', 'click', '[data-cart-add-id]', (e) => {
		let target = e.target.closest('[data-cart-add-id]');

		addItem(
			target.dataset.cartAddId,
			target.dataset.cartAddQuantity ? target.dataset.cartAddQuantity : 1
		);
	});

	on('body', 'submit', 'form[action="/cart/add"]', (e) => {
		e.preventDefault();
		let form = e.target;
		addItem(
			form.querySelector('[name="id"]').value,
			form.querySelector('[name="quantity"]').value
		);
	});
};

const initBlogIndex = () => {
	if (document.querySelector('.blog-pagination-next')) {
		let infiniteScroll = new InfiniteScroll('.blog-load-more', {
			path: '.blog-pagination-next',
			append: '.blog-post',
			button: '.blog-load-more-trigger',
			scrollThreshold: false,
			history: false,
		});

		infiniteScroll.on('last', (body, path) => {
			let loadMoreTrigger = document.querySelector('.blog-load-more-trigger');

			loadMoreTrigger.style.display = 'block';
			loadMoreTrigger.disabled = true;
			loadMoreTrigger.innerHTML = 'You’ve reached the end';
		});
	}
};

const initAccount = () => {
	// account gate
	const gateWrapper = document.querySelector('.account-gate-wrapper');
	const gateContent = document.querySelector('.account-gate');
	const resetPasswordTrigger = document.querySelector(
		'.reset-password-trigger'
	);
	const resetPasswordCancel = document.querySelector('.reset-password-cancel');

	if (gateWrapper) {
		resetPasswordTrigger.addEventListener('click', (event) => {
			event.preventDefault();

			// update URL with hash
			history.pushState(null, null, '#recover');

			// assign active state
			root.classList.add('reset-password-active');

			// update wrapper height to fit account reset form
			gateWrapper.style.height =
				document.querySelector('.account-reset').offsetHeight;

			// focus on email field
			document.querySelector('#account-recover-email').focus();
		});

		if (resetPasswordCancel) {
			resetPasswordCancel.addEventListener('click', (event) => {
				event.preventDefault();

				// remove has from URL
				history.pushState(null, null, ' ');

				// remove active state from root
				root.classList.remove('reset-password-active');

				// update wrapper height to fit account reset form
				gateWrapper.style.height = gateContent.offsetHeight;
			});
		}

		// trigger password recovery form if url contains the hash
		if (location.hash == '#recover') {
			resetPasswordTrigger.click();
		}

		// set initial height
		gateWrapper.style.height = gateContent.offsetHeight;
	}
};

const initFrontpage = () => {
	// our-menu
	const tabPill = document.querySelector('.tab-pill');

	on('body', 'click', '.tab', (e) => {
		const tab = e.target.closest('.tab');
		const direction = tab.dataset.direction;

		// our-menu: switch tab
		tabPill.classList.remove('left', 'right');
		tabPill.classList.add(direction);
		tab.classList.add('active');

		getSiblings(tab).forEach((el) => {
			el.classList.remove('active');
		});

		// our-menu: switch content
		const tab2content = document.querySelector('.tab-2-content');
		const tabContents = document.querySelector('.tab-contents');

		if (direction == 'right') {
			tab2content.classList.remove('hidden');
			tab2content.setAttribute('aria-hidden', false);
			tabContents.classList.add('js-tab-2-active');
			tabContents.setAttribute('aria-hidden', true);
		} else {
			tab2content.classList.add('hidden');
			tab2content.setAttribute('aria-hidden', true);
			tabContents.classList.remove('js-tab-2-active');
			tabContents.setAttribute('aria-hidden', false);
		}
	});

	// about
	const flickityPrev = document.querySelector(
		'.frontpage-about .flickity-button.previous'
	);
	const flickityNext = document.querySelector(
		'.frontpage-about .flickity-button.next'
	);

	on(
		'body',
		'click',
		'.frontpage-about .content-block.is-selected .btn-prev-next .prev',
		(e) => {
			flickityPrev ? flickityPrev.click() : '';
		}
	);

	on(
		'body',
		'click',
		'.frontpage-about .content-block.is-selected .btn-prev-next .next',
		(e) => {
			flickityNext ? flickityNext.click() : '';
		}
	);

	// loyalty
	const targetParagraph = document.querySelector(
		'.global-loyalty .text .paragraph'
	);
	const targetParagraphText = document.querySelector(
		'.global-loyalty .text .paragraph'
	).innerText;
	const paragraph_mobile = document.querySelector(
		'.global-loyalty .paragraph_mobile'
	)
		? document.querySelector('.global-loyalty .paragraph_mobile').value
		: '';

	const targetCtaLabel = document.querySelector('.global-loyalty .text .btn');
	const targetCtaLabelText = document.querySelector(
		'.global-loyalty .text .btn'
	).innerText;
	const cta_label_mobile = document.querySelector(
		'.global-loyalty .cta_label_mobile'
	)
		? document.querySelector('.global-loyalty .cta_label_mobile').value
		: '';

	if (paragraph_mobile) {
		initReplaceContentForMobile(
			targetParagraph,
			targetParagraphText,
			paragraph_mobile
		);
	}
	if (cta_label_mobile) {
		initReplaceContentForMobile(
			targetCtaLabel,
			targetCtaLabelText,
			cta_label_mobile
		);
	}
};

// const initFAQ = () => {
// 	on('body', 'keyup', '.faq-side-panel .field input', (e) => {
// 		const searchKeyWord = e.target.value.toLowerCase();
// 		const accordions = document.querySelectorAll('.comp-accordion');

// 		accordions.forEach((accordion) => {
// 			accordion.classList.add('is-hidden');
// 			const answer = accordion.innerText.toLowerCase();

// 			if (answer.indexOf(searchKeyWord) > -1) {
// 				accordion.classList.remove('is-hidden');
// 			} else {
// 				accordion.classList.add('is-hidden');
// 			}
// 		});
// 	});
// };

const initStockist = () => {
	function initSidePanel() {
		// change input placholder
		document.querySelector('.stockist-search-field').placeholder =
			'Enter Address or zip';

		// insert shopify section into stockist panel
		const stockistPanelForm = document.querySelector('.stockist-search-form');
		const shopifyPanel = document
			.querySelector('.stockist-side-panel-top')
			.cloneNode(true);
		stockistPanelForm.before(shopifyPanel);
	}

	function initMap() {
		const map = document.querySelector('.stockist-map');
		map.classList.add('js-slide-in');
	}

	function initMobileStockist() {
		// styling stockist
		const stockist = document.querySelector('#stockist-widget');

		if (isMobile || isTablet) {
			stockist.classList.add('mobile-view');
		} else {
			stockist.classList.remove('mobile-view');
		}
	}

	window.addEventListener('resize', function () {
		initMobileStockist();
	});

	window.addEventListener('load', function () {
		setTimeout(() => {
			initSidePanel();
			initMap();
			initMobileStockist();
		}, 1000);
	});
};

// execute pieces and components functions
initComboBox();
initAccordions();
initFlickitySlider();
initMarquee();
initFields();

// execute global and component functions
initPageTransition();
initPageAnimation();
initContentLayout();
initArrows();
initPopUpMessage();
initEmailCapture();

// execute page specific functions
switch (root.id) {
	case 'template-frontpage':
		initFrontpage();
		break;

	// case 'template-page-faq':
	// 	initFAQ();
	// 	break;

	case 'template-page-store-locator':
		initStockist();
		break;

	case 'template-page-gifting':
		document.querySelectorAll('.content-block').forEach((block) => {
			const target = block.querySelector('.comp-img-text .btn');
			const originalContent = target.innerText;
			const newContent = block.querySelector('.cta_label_mobile')
				? block.querySelector('.cta_label_mobile').value
				: '';

			if (newContent) {
				initReplaceContentForMobile(target, originalContent, newContent);
			}
		});

		break;

	case 'template-blog-index':
		initBlogIndex();
		break;

	case 'template-account':
		initAccount();
		break;
}

// make visible .avoid-style-flash elements
setTimeout(() => {
	document.querySelectorAll('.avoid-style-flash').forEach((el) => {
		el.style.visibility = 'visible';
	});
}, 400);
